import {
    ILayerProps,
    IMarkupLayerProps,
    IMarkupPoint,
    MarkupDasharrayType,
    MarkupLabelType,
    MarkupLabelUnit,
    MarkupLinecapType,
    MarkupTextPositionType,
    MarkupType,
} from '@/version-types';
import LabelKit from './label-kit';
import { computedSegmentBaseInfo, MARKUP_POINT_SIZE } from './layer';

/**
 * 修正绘制最终层宽高和定位
 *
 * @param layer - 图层属性
 * @param operateLayer - 可选；操作图层
 */
export function fixDrawResult(layer: ILayerProps, operateLayer?: ILayerProps | null) {
    const data = layer.data as IMarkupLayerProps;

    // 指引线
    if (data.markupType === MarkupType.Segment) {
        if (operateLayer) {
            computedSegmentBaseInfo(layer, operateLayer);
        }
        return;
    }

    // 尺寸标注
    const {
        label,
        fill,
        anchor: { arrowLeft, arrowRight },
        startPoint,
        endPoint,
    } = data;
    const {
        renderArea: { up, down },
    } = LabelKit.getLabelRenderInfo(layer.width, fill.width, layer);
    const leftPointHeight = startPoint ? (startPoint.height / MARKUP_POINT_SIZE) * fill.width : 0;
    const rightPointHeight = endPoint ? (endPoint.height / MARKUP_POINT_SIZE) * fill.width : 0;
    const halfPointHeight = Math.ceil(Math.max(leftPointHeight / 2, rightPointHeight / 2));

    const upResult = Math.max(up, arrowLeft[0], arrowRight[0], halfPointHeight);
    const downResult = Math.min(down, arrowLeft[1], arrowRight[1], -halfPointHeight);
    const originHalfHeight = Math.floor(layer.height / 2);
    layer.height += upResult - originHalfHeight;
    data.centerOffset.y = upResult;
    layer.height += -originHalfHeight - downResult;
}

export function fixLabelSize(layer: ILayerProps) {
    const { label } = layer.data as IMarkupLayerProps;
    const kit = new LabelKit();
    const { fontSize, width, height } = kit.getFontSize(layer, label.fontSize, layer.width);
    label.fontSize = fontSize;
    label.width = width;
    label.height = height;
    label.roundWeight = LabelKit.getRoundWeight(layer); // label.background ? height : 0;
    kit.destroy();
}

export function setLabelType(
    layer: ILayerProps,
    type: MarkupLabelType,
    text: string,
    num: number,
    units: MarkupLabelUnit[],
    operateLayer?: ILayerProps | null,
) {
    const { label } = layer.data as IMarkupLayerProps;
    const oldText = LabelKit.getRenderText(label);
    label.type = type;
    label.text = text;
    label.num = num;
    label.units = units;
    const newText = LabelKit.getRenderText(label);
    if (newText !== oldText) {
        fixLabelSize(layer);
    }
    fixDrawResult(layer, operateLayer);
}

export function setLabelPosition(
    layer: ILayerProps,
    position: MarkupTextPositionType,
    operateLayer?: ILayerProps | null,
) {
    const { label } = layer.data as IMarkupLayerProps;
    label.position = position;
    fixDrawResult(layer, operateLayer);
}

export function setLabelRotate(layer: ILayerProps, rotate: number, operateLayer?: ILayerProps | null) {
    const { label } = layer.data as IMarkupLayerProps;
    label.rotate = rotate;
    fixDrawResult(layer, operateLayer);
}

export function setLabelFontFamily(
    layer: ILayerProps,
    fontFamily: string,
    materialIds: any,
    operateLayer?: ILayerProps | null,
) {
    const { label } = layer.data as IMarkupLayerProps;
    label.fontFamily = fontFamily;
    layer.materialIds = materialIds;
    fixLabelSize(layer);
    fixDrawResult(layer, operateLayer);
}

export function setLabelFontSize(layer: ILayerProps, fontSize: number, operateLayer?: ILayerProps | null) {
    const data = layer.data as IMarkupLayerProps;
    data.label.fontSize = fontSize;
    fixLabelSize(layer);
    fixDrawResult(layer, operateLayer);
}

export function setLabelColor(layer: ILayerProps, color: string) {
    const { label } = layer.data as IMarkupLayerProps;
    label.color = color;
}

export function setLabelStyle(layer: ILayerProps, bold: boolean, italic: boolean, operateLayer?: ILayerProps | null) {
    const data = layer.data as IMarkupLayerProps;
    const { label } = data;
    label.bold = bold;
    label.italic = italic;
    fixLabelSize(layer);
    fixDrawResult(layer, operateLayer);
}

export function setLabelBackground(layer: ILayerProps, background: string, operateLayer?: ILayerProps | null) {
    const { label } = layer.data as IMarkupLayerProps;
    const oldHasBg = !!label.background;
    const newHasBg = !!background;
    label.background = background;
    label.roundWeight = LabelKit.getRoundWeight(layer); // background ? label.height : 0;
    if (oldHasBg !== newHasBg) {
        fixDrawResult(layer, operateLayer);
    }
}

export function setFillColor(layer: ILayerProps, color: string) {
    const data = layer.data as IMarkupLayerProps;
    data.fill.color = color;
}

export function setFillStyle(layer: ILayerProps, dasharray: MarkupDasharrayType, linecap: MarkupLinecapType) {
    const data = layer.data as IMarkupLayerProps;
    data.fill.dasharray = dasharray;
    data.fill.linecap = linecap;
}

export function setFillWidth(layer: ILayerProps, width: number, operateLayer?: ILayerProps | null) {
    const data = layer.data as IMarkupLayerProps;
    data.fill.width = width;
    fixDrawResult(layer, operateLayer);
}

export function setPoint(layer: ILayerProps, point: IMarkupPoint, isStart: boolean, operateLayer?: ILayerProps | null) {
    const data = layer.data as IMarkupLayerProps;
    if (isStart) {
        data.startPoint = point;
    } else {
        data.endPoint = point;
    }
    fixDrawResult(layer, operateLayer);
}

export function setSwap(layer: ILayerProps, startPoint: IMarkupPoint, endPoint: IMarkupPoint) {
    const data = layer.data as IMarkupLayerProps;
    data.startPoint = startPoint;
    data.endPoint = endPoint;
}

export function setLine(layer: ILayerProps, hasLine: boolean) {
    const data = layer.data as IMarkupLayerProps;
    data.hasLine = hasLine;
}
